import { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useSearchParams } from "@/lib/router-compat";
import { cn } from "@/lib/utils";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import OptimizedImage from "@/components/OptimizedImage";
import { supabase } from "@/integrations/supabase/client";

// Fallback images for when no DB projects exist
import kitchenImg from "@/assets/project-kitchen.jpg";
import hotelImg from "@/assets/project-hotel.jpg";
import officeImg from "@/assets/project-office.jpg";
import heroImg from "@/assets/hero-interior.jpg";

const categories = ["Tous", "Résidentiel", "Professionnel", "Infographie 3D"];

interface Project {
  id: string;
  title: string;
  slug: string;
  category: string;
  location: string | null;
  cover_image: string | null;
  description: string | null;
  cover_position: string | null;
  client_type: string | null;
}

const fallbackProjects: Project[] = [
  { id: "1", title: "Résidence contemporaine", slug: "residence", category: "Architecture d'intérieur", cover_image: kitchenImg, location: "Lyon 6ème", description: null, cover_position: null, client_type: "Particulier" },
  { id: "2", title: "Hôtel Boutique", slug: "hotel", category: "Architecture d'intérieur", cover_image: hotelImg, location: "Vieux Lyon", description: null, cover_position: null, client_type: "Professionnel - Hôtel" },
  { id: "3", title: "Espace de coworking", slug: "coworking", category: "Architecture d'intérieur", cover_image: officeImg, location: "Grézieu-la-Varenne", description: null, cover_position: null, client_type: "Particulier" },
  { id: "4", title: "Appartement haussmannien", slug: "haussmannien", category: "Infographie 3D", cover_image: heroImg, location: "Lyon 2ème", description: null, cover_position: null, client_type: "Particulier" },
];

const Projets = () => {
  const [searchParams] = useSearchParams();
  const initialFilter = categories.includes(searchParams.get("filtre") || "") ? searchParams.get("filtre")! : "Tous";
  const [activeCategory, setActiveCategory] = useState(initialFilter);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      const { data } = await supabase
        .from("projects")
        .select("id, title, slug, category, location, cover_image, description, cover_position, client_type")
        .eq("published", true)
        .order("display_order");
      if (data && data.length > 0) {
        setProjects(data as Project[]);
      } else {
        setProjects(fallbackProjects);
      }
      setLoading(false);
    };
    fetchProjects();
  }, []);

  const filtered = activeCategory === "Tous"
    ? projects
    : activeCategory === "Résidentiel"
      ? projects.filter((p) => p.category === "Architecture d'intérieur" && (!p.client_type || !p.client_type.startsWith("Professionnel")))
      : activeCategory === "Professionnel"
        ? projects.filter((p) => p.client_type?.startsWith("Professionnel"))
        : projects.filter((p) => p.category === activeCategory);

  return (
    <Layout>
      <SEOHead
        title="Projets - JIIA Studio | Portfolio Architecture d'intérieur Lyon"
        description="Découvrez les réalisations de JIIA Studio : projets d'architecture d'intérieur et d'infographie 3D à Lyon et dans l'Ouest Lyonnais."
        canonical="/projets"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Projets - JIIA Studio",
          description: "Portfolio de réalisations en architecture d'intérieur et infographie 3D par JIIA Studio à Lyon Ouest.",
          url: "https://jiia-studio.com/projets",
          isPartOf: { "@type": "WebSite", name: "JIIA Studio", url: "https://jiia-studio.com" },
        }}
      />

      <section className="py-24 md:py-32">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">Portfolio</p>
            <h1 className="font-heading text-4xl md:text-5xl font-light mb-12">Mes réalisations</h1>
          </AnimatedSection>

          <div className="flex flex-wrap gap-3 md:gap-4 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "text-sm font-body uppercase tracking-widest pb-1 border-b-2 transition-colors",
                  activeCategory === cat
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-muted-foreground font-body">Chargement...</p>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {filtered.map((project, i) => (
                  <AnimatedSection key={project.id} delay={i * 0.1}>
                    <Link to={`/projets/${project.slug}`} className="group block">
                      <div className="aspect-[4/3] overflow-hidden rounded-sm mb-4">
                        {project.cover_image ? (
                          <OptimizedImage
                            src={project.cover_image}
                            alt={project.title}
                            width={800}
                            className="w-full h-full group-hover:scale-105 transition-transform duration-700"
                            style={{ objectPosition: project.cover_position || 'center center' }}
                            priority={i < 2}
                          />
                        ) : (
                          <div className="w-full h-full bg-secondary flex items-center justify-center">
                            <p className="text-muted-foreground text-xs font-body">Image à venir</p>
                          </div>
                        )}
                      </div>
                      <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-1">
                        {project.category}{project.location ? ` · ${project.location}` : ""}
                      </p>
                      <p className="text-xs font-body uppercase tracking-[0.25em] text-accent mb-1">
                        {project.slug
                          .split("-")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(" ")}
                      </p>
                      <h3 className="font-heading text-xl group-hover:text-accent transition-colors">{project.title}</h3>
                    </Link>
                  </AnimatedSection>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Projets;
